import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import {
  ATTRIBUTION_STORAGE_KEY,
  attributionFromQuery,
  CheckoutTracking,
  collectCheckoutTracking,
  EntryAttribution,
} from "../../utils/tracking-ids";
import { GtmUserData } from "../../utils/gtm-user-data";

declare global {
  interface Window {
    /** CMP CookieScript ładowany przez GTM. */
    CookieScript?: { instance?: { currentState?: () => unknown } };
  }
}

const PURCHASE_USER_DATA_KEY = "p24PurchaseUserData:";
const PURCHASE_LOCK_KEY = "gtmPurchaseTracked:";

/**
 * Stan pomiaru po stronie przeglądarki (wytyczne pomiaru, zadania 1 i 7):
 * - atrybucja z URL wejścia do `/creator` (`gclid`, `gbraid`, `wbraid`,
 *   `fbclid`) w sessionStorage,
 * - zebranie identyfikatorów (ciasteczka GA / Google Ads / Meta, zgody
 *   CookieScript) do zapisu w rezerwacji,
 * - `user_data` zakupu przechowane na czas płatności w bramce (ta sama karta),
 * - lokalna blokada ponownego `purchase` (localStorage, klucz per rezerwacja).
 *
 * Każdy dostęp do storage jest w try/catch – tryb prywatny / zablokowane
 * ciasteczka nie mogą zatrzymać kreatora.
 */
@Injectable({ providedIn: "root" })
export class CheckoutTrackingService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly document = inject(DOCUMENT);

  /** Wejście do kreatora: zapamiętuje identyfikatory kliknięć z URL. */
  captureEntryAttribution(
    params: { get(name: string): string | null } | null | undefined,
  ): void {
    if (!this.isBrowser || !params) return;
    let fresh: EntryAttribution | null = null;
    try {
      fresh = attributionFromQuery(params, Date.now());
    } catch {
      return;
    }
    if (!fresh) return;
    this.write(sessionStorage, ATTRIBUTION_STORAGE_KEY, {
      ...this.entryAttribution(),
      ...fresh,
    });
  }

  entryAttribution(): EntryAttribution | null {
    return this.read<EntryAttribution>(sessionStorage, ATTRIBUTION_STORAGE_KEY);
  }

  /** Identyfikatory do `finishReservation` – nigdy nie rzuca. */
  collect(): CheckoutTracking | null {
    if (!this.isBrowser) return null;
    try {
      let cookieScriptState: unknown = null;
      try {
        cookieScriptState = window.CookieScript?.instance?.currentState?.();
      } catch {
        /* CMP niezaładowany / błąd API – zgody nieznane */
      }
      return collectCheckoutTracking({
        cookies: this.document.cookie ?? "",
        attribution: this.entryAttribution(),
        cookieScriptState,
      });
    } catch {
      return null;
    }
  }

  /**
   * `user_data` zakupu na czas płatności w bramce. sessionStorage przeżywa
   * przekierowanie w tej samej karcie; w innej przeglądarce `purchase` pójdzie
   * bez `user_data` (dane osobowe nie są w publicznym odczycie rezerwacji).
   */
  rememberPurchaseUserData(reservationId: string, userData: GtmUserData | undefined): void {
    if (!this.isBrowser || !userData) return;
    this.write(sessionStorage, PURCHASE_USER_DATA_KEY + reservationId, userData);
  }

  purchaseUserData(reservationId: string): GtmUserData | undefined {
    return (
      this.read<GtmUserData>(sessionStorage, PURCHASE_USER_DATA_KEY + reservationId) ??
      undefined
    );
  }

  forgetPurchaseUserData(reservationId: string): void {
    this.remove(sessionStorage, PURCHASE_USER_DATA_KEY + reservationId);
  }

  isPurchaseLocked(reservationId: string): boolean {
    return this.read<boolean>(localStorage, PURCHASE_LOCK_KEY + reservationId) === true;
  }

  lockPurchase(reservationId: string): void {
    this.write(localStorage, PURCHASE_LOCK_KEY + reservationId, true);
  }

  private read<T>(storage: Storage, key: string): T | null {
    if (!this.isBrowser) return null;
    try {
      const raw = storage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }

  private write(storage: Storage, key: string, value: unknown): void {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage niedostępny – działamy bez zapisu */
    }
  }

  private remove(storage: Storage, key: string): void {
    if (!this.isBrowser) return;
    try {
      storage.removeItem(key);
    } catch {
      /* jw. */
    }
  }
}
